'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'motion/react';
import { RiInformationLine, RiPauseLine, RiPlayLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { hl } from '@/lib/rich-text';
import { sampleForMode } from '@/lib/video-samples';

import './creative.css';

/**
 * "Bir fotoğraf girdi, bu video çıktı" — kreatif sayfasının ilk sözü.
 *
 * Örnek UYDURMA DEĞİL: `lib/video-samples.ts`teki ürün örneği, ürünün
 * kendisiyle üretilmiş ve vitrine alınmış gerçek çıktı (kurgu marka
 * "Kynure"). Panelin örnek kartı da aynı dosyayı kullanıyor.
 *
 * 🔴 Kütüphanedeki öteki iki örnek BURADA YOK:
 *   · şablon örneğinin kaynağı tanınmış bir spor ayakkabı (dosya başlığında),
 *   · sahne-tarifi örneğinin girdisi başka bir siteden alınmış ekran
 *     görüntüsü — üstünde o sitenin kapat/ok düğmeleri duruyor ve gözlük
 *     tanınır bir markaya benziyor. Herkese açık tanıtım sayfasında
 *     başkasının görselini göstermek, panelde göstermekten farklı bir iş.
 *
 * ── Video ─────────────────────────────────────────────────────────────────
 *
 * Sessiz, döngülü ve YALNIZ görünürken oynuyor (`useInView`, once değil —
 * ekran dışında çözücü çalışmasın). Kendi oynat/duraklat düğmesi var: 5 sn'den
 * uzun, kendiliğinden oynayan içerik durdurulabilmeli (WCAG 2.2.2). Azaltılmış
 * hareket tercihinde kendiliğinden başlamıyor; kapak karesi duruyor, düğme
 * oynatıyor. Karar efektte — `autoPlay` niteliği basılmıyor, sunucuyla
 * istemcinin DOM'u aynı.
 *
 * Girdi ile çıktı arasındaki ok bir dönüşümü anlatıyor: içinden bir ışık
 * geçiyor (CSS, yalnız transform; azaltılmış harekette yok).
 */
export function CreativeShowcase() {
  const t = useTranslations('featuresCreative.showcase');
  const uid = useId();
  const sample = sampleForMode('product');
  const reduce = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const inView = useInView(videoRef, { amount: 0.4 });
  /* Kullanıcı elle durdurduysa görünür olunca yeniden başlatma. */
  const [userPaused, setUserPaused] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (inView && !reduce && !userPaused) {
      void video.play().catch(() => setPlaying(false));
    } else if (!inView) {
      video.pause();
    }
  }, [inView, reduce, userPaused]);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setUserPaused(false);
      void video.play().catch(() => setPlaying(false));
    } else {
      setUserPaused(true);
      video.pause();
    }
  };

  if (!sample?.inputSrc) return null;

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="crs">
        <figure className="crs-in">
          <div className="crs-in-frame">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="crs-in-img"
              src={sample.inputSrc}
              alt={t('inputAlt')}
              width={520}
              height={520}
              loading="lazy"
              decoding="async"
            />
          </div>
          <figcaption className="crs-cap">
            <span className="crs-label">{t('inputLabel')}</span>
            <dl className="crs-fields">
              <div>
                <dt>{t('fields.brand')}</dt>
                <dd>Kynure</dd>
              </div>
              <div>
                <dt>{t('fields.product')}</dt>
                <dd>{t('fields.productValue')}</dd>
              </div>
            </dl>
          </figcaption>
        </figure>

        <div className="crs-arrow" aria-hidden="true">
          <span className="crs-arrow-line">
            <span className="crs-arrow-light" />
          </span>
          <span className="crs-arrow-chip">{t('arrow')}</span>
        </div>

        <figure className="crs-out">
          <div className="crs-video">
            <video
              ref={videoRef}
              className="crs-video-el"
              src={sample.videoSrc}
              poster={sample.posterSrc}
              width={360}
              height={640}
              muted
              loop
              playsInline
              preload="metadata"
              aria-label={t('videoAlt')}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            <motion.button
              type="button"
              className="crs-play"
              onClick={toggle}
              aria-label={playing ? t('pause') : t('play')}
              whileTap={{ scale: 0.92 }}
            >
              {playing ? (
                <RiPauseLine size={18} aria-hidden="true" />
              ) : (
                <RiPlayLine size={18} aria-hidden="true" />
              )}
            </motion.button>
          </div>
          <figcaption className="crs-cap">
            <span className="crs-label crs-label--out">{t('outputLabel')}</span>
            <p className="crs-out-spec">{t('outputSpec')}</p>
          </figcaption>
        </figure>
      </div>

      <p className="ps-note">
        <RiInformationLine size={16} aria-hidden="true" />
        {t('note')}
      </p>
    </ProductSection>
  );
}
